"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AltiumSheetEntryLocalRepeatLocalNamingRule = void 0;
const _ = require("lodash");
const AltiumLabelTypes_1 = require("./AltiumLabelTypes");
const AltiumLabelTypeNameUtil_1 = require("./AltiumLabelTypeNameUtil");
const AltiumNetLocalNamingRule_1 = require("./AltiumNetLocalNamingRule");
class AltiumSheetEntryLocalRepeatLocalNamingRule extends AltiumNetLocalNamingRule_1.AltiumNetLocalNamingRule {
    apply(hierarchy) {
        if (!this.config.allowSheetEntryNetNames) {
            return;
        }
        this.recombobulateRepeatedSheetEntries(hierarchy);
    }
    recombobulateRepeatedSheetEntries(hierarchy) {
        const repeatSheetEntries = [];
        for (const obj of hierarchy.combobulator.defaultDimension.iterateLabelObjects()) {
            if (obj.label?.labelType === 'sheet-entry' && obj.label.strategyMetadata?.['sheet-entry-metadata']?.isRepeated) {
                repeatSheetEntries.push(obj);
            }
        }
        for (const repeatSheetEntry of repeatSheetEntries) {
            const labelStrategyMetadata = repeatSheetEntry.label?.strategyMetadata?.['sheet-entry-metadata'];
            if (!labelStrategyMetadata) {
                throw new Error(`Sheet entry label is missing metadata`);
            }
            const endpoints = Array.from(new Set(repeatSheetEntry.endpoints.map((e) => e.dimension)));
            if (endpoints.length > 1) {
                throw new Error(`Repeat sheet entry ${repeatSheetEntry.label?.formattedLabel} is connected to multiple dimensions`);
            }
            const dimension = hierarchy.combobulator.getDimension(endpoints[0]);
            const connectedObjs = Array.from(dimension.getObjectsConnectedToPosition(repeatSheetEntry.endpoints[0].position));
            const connectedLabelNetObjects = connectedObjs.filter((o) => o.label?.labelType === 'net-label');
            const useLabelNetObject = connectedLabelNetObjects[0];
            if (!useLabelNetObject?.label) {
                continue;
            }
            if (connectedLabelNetObjects.length > 1) {
                const uniqueLabels = new Set(connectedLabelNetObjects.map((o) => o.label?.formattedLabel));
                if (uniqueLabels.size > 1) {
                    throw new Error(`Repeat sheet entry ${repeatSheetEntry.label?.formattedLabel} is connected to multiple labels: ${Array.from(uniqueLabels).join(', ')}`);
                }
            }
            const netLabel = useLabelNetObject.label;
            let parsedName;
            try {
                parsedName = AltiumLabelTypeNameUtil_1.AltiumLabelTypeNameUtil.parseLabelTypeName(netLabel.originalLabel);
            }
            catch (e) {
                if (e && e instanceof Error && e.message !== 'Not a label type name string') {
                    throw e;
                }
                parsedName = {
                    labelType: 'net-label',
                    name: netLabel.originalLabel,
                    argument: undefined,
                };
            }
            if (!connectedObjs.some((connectedObj) => connectedObj === repeatSheetEntry)) {
                throw new Error(`Repeat sheet entry ${repeatSheetEntry.label?.formattedLabel} is not connected to itself`);
            }
            for (const obj of connectedObjs) {
                dimension.removeObject(obj);
            }
            const busMembersByChannel = AltiumSheetEntryLocalRepeatLocalNamingRule.getTappedBusRangeMembersByNumericIdentity(connectedObjs, labelStrategyMetadata.sheetEntryName);
            for (const channelIndex of labelStrategyMetadata.channelIndexes) {
                const memberName = busMembersByChannel.get(channelIndex);
                const rawParsedName = memberName ?? `${parsedName.name}${channelIndex}`;
                const newLabelName = AltiumLabelTypeNameUtil_1.AltiumLabelTypeNameUtil.getLabelTypeName(parsedName.labelType, rawParsedName, parsedName.argument);
                const newDimension = hierarchy.combobulator.getOrCreateDimension((0, AltiumLabelTypes_1.nameAltiumBusDimension)(parsedName.name, rawParsedName));
                for (let obj of connectedObjs) {
                    obj = {
                        ...structuredClone(_.omit(obj, 'net', 'netRef')),
                        net: undefined,
                        netRef: hierarchy.combobulator.getUniqueNetRef(newLabelName),
                    };
                    if (obj.wire) {
                        newDimension.addWireObject({
                            ...obj,
                            endpoints: obj.endpoints.map((e) => ({ ...e, dimension: newDimension.name })),
                        });
                    }
                    if (obj.label) {
                        if (obj.appliesNetName === (obj.label.formattedLabel ?? obj.label.originalLabel)) {
                            obj.appliesNetName = newLabelName;
                            obj.label.formattedLabel = newLabelName;
                        }
                        newDimension.addPoint(obj.label.position, obj);
                        if (obj.label.strategyMetadata?.['sheet-entry-metadata']) {
                            obj.label.strategyMetadata['sheet-entry-metadata'].channelIndex = channelIndex;
                        }
                    }
                    if (obj.connection) {
                        throw new Error(`Connections not yet supported in sheet entry repeat rule`);
                    }
                }
                if (memberName !== undefined) {
                    hierarchy.combobulator.setLabel([{ position: repeatSheetEntry.endpoints[0].position, dimension: newDimension.name }], memberName, 'net-label', { busRangeMember: true });
                }
            }
        }
        for (const child of hierarchy.children) {
            this.recombobulateRepeatedSheetEntries(child);
        }
    }
    static getTappedBusRangeMembersByNumericIdentity(connectedObjs, entryName) {
        const members = new Map();
        for (const obj of connectedObjs) {
            for (const member of obj.wire?.strategyMetadata?.busTapMembers ?? []) {
                const memberIndex = /(\d+)$/.exec(member)?.[1];
                if (memberIndex === undefined) {
                    continue;
                }
                const channelIndex = Number(memberIndex);
                if (member.toLowerCase() !== `${entryName}${channelIndex}`.toLowerCase()) {
                    continue;
                }
                const existing = members.get(channelIndex);
                if (existing === undefined) {
                    members.set(channelIndex, member);
                }
            }
        }
        return members;
    }
}
exports.AltiumSheetEntryLocalRepeatLocalNamingRule = AltiumSheetEntryLocalRepeatLocalNamingRule;
//# sourceMappingURL=AltiumSheetEntryLocalRepeatLocalNamingRule.js.map